import React, { useRef } from 'react';

import { useForkRef } from '../hooks/useForkRef';
import { compose } from '../lib/compose';
import { ARRAY_EMPTY } from '../lib/emptyStubs';
import { OverlayTreeItem } from '../OverlayTreeItem';
import { Portal } from '../Portal';
import { withHideOffscreen } from './_hideOffscreen/Popup_hideOffscreen';
import { withNonvisual } from './_nonvisual/Popup_nonvisual';
import { withTargetAnchor } from './_target/Popup_target_anchor';
import { cnPopup } from './Popup.cn';
import type { PopupProps } from './Popup.types';
import type { PopupSlotProps } from './Slot';
import { PopupTail as Tail } from './Tail/Popup-Tail';

import './Popup.css';

export type { Direction } from '../hooks/usePopper';
export { directions } from '../hooks/usePopper';

const enhance = compose(withHideOffscreen, withNonvisual, withTargetAnchor);

/** Компонент для создания всплывающего окна (попапа) */
export const Popup: React.FC<PopupProps> = enhance(
    ({
        slotTop,
        slotBottom,
        children,
        className,
        direction,
        hasTail,
        innerRef,
        keepMounted,
        scope,
        style,
        tailRef,
        tailSize,
        visible: givenVisible = false,
        zIndex,
        disableOverlay,
        unstable_onRenderTail,
        onClose,
        unstable_essentialRefs = ARRAY_EMPTY,
        onClick,
        overlayCloseStrategy = 'pressdown',
        overlayTreeItemName,
        // Извлекаем свойства, т.к. они не нужны на DOM узле
        // FIXME: https://github.com/bem/bem-react/issues/381
        nonvisual: _nonvisual,
        hideOffscreen: _hideOffscreen,
        checkLastCloses,
        ...props
    }: PopupProps) => {
        const containerRef = useRef(null);
        const overlayVisible = givenVisible && !disableOverlay;

        const getSlot = React.useCallback(
            (addon?: React.ReactNode, place?: PopupSlotProps['place']) => {
                if (React.isValidElement(addon)) {
                    return React.cloneElement(addon, { ...addon.props, place });
                }
                return addon;
            },
            [],
        );

        return (
            <OverlayTreeItem
                onClose={onClose}
                visible={overlayVisible}
                essentialRefs={[containerRef, ...unstable_essentialRefs]}
                closeStrategy={overlayCloseStrategy}
                overlayTreeItemName={overlayTreeItemName}
                checkLastCloses={checkLastCloses}
            >
                <Portal scope={scope} visible={givenVisible} keepMounted={keepMounted}>
                    <div
                        {...props}
                        className={cnPopup({ visible: givenVisible }, [className])}
                        data-popper-placement={direction}
                        onClick={onClick}
                        ref={useForkRef(containerRef, innerRef)}
                        style={{ ...style, zIndex }}
                    >
                        {getSlot(slotTop, 'top')}
                        {typeof children === 'function' ? children({ tailRef }) : children}
                        {getSlot(slotBottom, 'bottom')}
                        {unstable_onRenderTail &&
                            unstable_onRenderTail(
                                <Tail
                                    ref={tailRef}
                                    style={{ height: tailSize, width: tailSize }}
                                />,
                            )}
                        {!unstable_onRenderTail && hasTail && (
                            <Tail ref={tailRef} style={{ height: tailSize, width: tailSize }} />
                        )}
                    </div>
                </Portal>
            </OverlayTreeItem>
        );
    },
);

Popup.displayName = cnPopup();
